import type { Bootstrap, MediaEntry } from '../api/schema.js'

/**
 * blob으로 통째 버퍼링해도 되는 자산의 상한.
 *
 * 스펙 §4.2는 변환이 끝나기 전까지 원본을 그대로 서빙하고 매니페스트에도 원본의
 * 실제 바이트 수를 싣는다고 못박는다 — 즉 매니페스트가 변환 전 4K 폰 영상
 * 수백 MB를 가리키는 구간이 반드시 존재한다. 스트림을 청크 배열에 모았다가 Blob으로
 * 복사하는 이 경로의 최대 사용량은 파일 크기의 약 2배라, 그런 파일을 만나면 아이폰
 * 탭이 그냥 죽는다. 그리고 그건 하객 전원에게 동시에 일어난다.
 *
 * 32MB는 변환된 720×1280 CRF 26 필름(초당 약 0.25MB) 기준으로 2분이 넘는 분량 —
 * 두 필름 어느 쪽보다도 길다 — 이면서 최대 사용량을 64MB 안쪽에 묶는다.
 * 이 상한을 넘거나 아직 변환되지 않은(`optimized: false`) 자산은 버퍼링하지 않고
 * 섹션이 네트워크 URL로 직접 스트리밍하게 둔다. 스크럽이 거칠어질 뿐 영상은 나온다.
 */
export const MAX_BUFFERED_ASSET_BYTES = 32 * 1024 * 1024

export interface FontManifestEntry {
  family: string
  weight: string
  url: string
  bytes: number
}

/**
 * `blob`은 통째로 받아 objectURL로 만든다(캐시가 소비자에게 전달한다).
 * `prefetch`는 브라우저 HTTP 캐시만 데운다 — `<img src>`가 곧 같은 URL을 쓰므로
 * 메모리에 blob을 붙들고 있을 이유가 없다.
 */
export type LoadStrategy = 'blob' | 'prefetch'

export interface LoadItem {
  id: string
  url: string
  bytes: number
  kind: 'media' | 'font'
  strategy: LoadStrategy
  family?: string
  weight?: string
}

export interface LoadPlan {
  /** 로딩 화면이 끝날 때까지 기다리는 것들 (전부 blob) */
  critical: LoadItem[]
  /** 진입 직후 백그라운드로 이어받는 것들 */
  deferred: LoadItem[]
  totalBytes: number
}

function fontItem(font: FontManifestEntry): LoadItem {
  return {
    id: `font:${font.family}:${font.weight}`,
    url: font.url,
    bytes: font.bytes,
    kind: 'font',
    strategy: 'blob',
    family: font.family,
    weight: font.weight,
  }
}

export function canBuffer(entry: MediaEntry): boolean {
  return entry.optimized && entry.bytes <= MAX_BUFFERED_ASSET_BYTES
}

function mediaItem(entry: MediaEntry, strategy: LoadStrategy): LoadItem {
  return { id: entry.key, url: entry.url, bytes: entry.bytes, kind: 'media', strategy }
}

function isDefined(item: LoadItem | undefined): item is LoadItem {
  return item !== undefined
}

export function buildLoadPlan(bootstrap: Bootstrap, fonts: FontManifestEntry[]): LoadPlan {
  // 버퍼링할 수 없는 critical 자산은 계획에서 빠진다 — 진입을 막지도, 진행률에
  // 잡히지도 않고, 섹션이 네트워크 URL로 바로 스트리밍한다.
  const critical: LoadItem[] = bootstrap.media
    .filter((entry) => entry.tier === 'critical')
    .map((entry) => (canBuffer(entry) ? mediaItem(entry, 'blob') : undefined))
    .filter(isDefined)

  critical.push(...fonts.map(fontItem))

  // 갤러리 사진은 blob으로 받아 봐야 <img>가 읽지 않는다 — prefetch로 강등해
  // 브라우저 HTTP 캐시만 데우고 메모리에는 남기지 않는다. 영상만 blob이 필요하다.
  const deferred: LoadItem[] = bootstrap.media
    .filter((entry) => entry.tier === 'deferred')
    .map((entry) => {
      if (entry.type === 'image') return mediaItem(entry, 'prefetch')
      return canBuffer(entry) ? mediaItem(entry, 'blob') : undefined
    })
    .filter(isDefined)

  return {
    critical,
    deferred,
    totalBytes: critical.reduce((sum, item) => sum + item.bytes, 0),
  }
}
